/**
 * Chase Orderbook Service
 * Dedicated orderbook tracking for chase operations
 * 
 * Key features:
 * - Multi-asset support (Map-based)
 * - Independent of terminal view
 * - Listener-based subscription model
 * - Auto-cleanup when no listeners
 * - Uses same SDK as BrowserOrderbookTracker
 */

import { WebSocketTransport, SubscriptionClient } from '@nktkas/hyperliquid';

export class ChaseOrderbookService {
    constructor() {
        // Map: asset -> { transport, subsClient, subscription, listeners, lastData }
        this.subscriptions = new Map();
    }

    /**
     * Subscribe to orderbook updates for an asset
     * Multiple chases can share the same asset subscription
     * @param {string} asset - Asset symbol (e.g., 'BTC', 'ETH')
     * @param {Function} listener - Callback function (data) => void
     */
    async subscribe(asset, listener) {
        // For HIP-3 assets (e.g., "xyz:TSLA"), preserve case of DEX prefix
        // Regular assets still get uppercased
        const key = asset.includes(':') ? asset : asset.toUpperCase();
        
        // If already subscribed, just add listener
        if (this.subscriptions.has(key)) {
            const sub = this.subscriptions.get(key);
            sub.listeners.add(listener);
            // console.log(`[CHASE-ORDERBOOK] Added listener to existing ${key} subscription (${sub.listeners.size} total)`);
            
            // Send last data to new listener if available
            if (sub.lastData) {
                listener(sub.lastData);
            }
            return;
        }
        
        // Create new subscription
        // console.log(`[CHASE-ORDERBOOK] Creating new subscription for ${key}`);
        
        try {
            // Create WebSocket transport (same config as BrowserOrderbookTracker)
            const transport = new WebSocketTransport({
                url: 'wss://api.hyperliquid.xyz/ws',
                timeout: 10000,
                keepAliveInterval: 30000,
                resubscribe: true,
                reconnect: {
                    maxRetries: Infinity,
                    connectionTimeout: 10000,
                    reconnectionDelay: (attempt) => Math.min(1000 * Math.pow(1.5, attempt), 30000)
                }
            });
            
            // Create subscription client
            const subsClient = new SubscriptionClient({ transport });
            
            // Wait for connection
            await transport.ready();
            // console.log(`[CHASE-ORDERBOOK] Connected to WebSocket for ${key}`);
            
            // Subscribe to L2 orderbook (same as BrowserOrderbookTracker)
            const subscription = await subsClient.l2Book(
                {
                    coin: key,
                    nSigFigs: null,  // Use default precision
                    mantissa: null,  // Use default mantissa
                    nLevels: 50      // Request 50 price levels
                },
                (data) => {
                    // Handle orderbook update
                    const sub = this.subscriptions.get(key);
                    if (!sub) return; // Subscription was removed
                    
                    // Parse data (same format as BrowserOrderbookTracker)
                    const parsedData = this._parseOrderbookData(data, key);
                    sub.lastData = parsedData;
                    
                    // Notify all listeners
                    sub.listeners.forEach(listener => {
                        try {
                            listener(parsedData);
                        } catch (error) {
                            // console.error(`[CHASE-ORDERBOOK] Listener error for ${key}:`, error);
                        }
                    });
                }
            );
            
            // Store subscription
            this.subscriptions.set(key, {
                transport,
                subsClient,
                subscription,
                listeners: new Set([listener]),
                lastData: null
            });
            
            // console.log(`[CHASE-ORDERBOOK] ✅ Subscribed to ${key} orderbook`);
            
        } catch (error) {
            // console.error(`[CHASE-ORDERBOOK] Failed to subscribe to ${key}:`, error);
            throw error;
        }
    }

    /**
     * Unsubscribe a listener from an asset
     * Closes WebSocket when no more listeners
     * @param {string} asset - Asset symbol
     * @param {Function} listener - Listener to remove
     */
    async unsubscribe(asset, listener) {
        // For HIP-3 assets, preserve case (same as subscribe)
        const key = asset.includes(':') ? asset : asset.toUpperCase();
        const sub = this.subscriptions.get(key);
        
        if (!sub) {
            // console.warn(`[CHASE-ORDERBOOK] No subscription found for ${key}`);
            return;
        }
        
        // Remove listener
        sub.listeners.delete(listener);
        // console.log(`[CHASE-ORDERBOOK] Removed listener from ${key} (${sub.listeners.size} remaining)`);
        
        // If no more listeners, close WebSocket
        if (sub.listeners.size === 0) {
            // console.log(`[CHASE-ORDERBOOK] No more listeners for ${key}, closing WebSocket`);
            
            try {
                // Unsubscribe from orderbook
                if (sub.subscription) {
                    await sub.subscription.unsubscribe();
                }
                
                // Close transport
                if (sub.transport) {
                    await sub.transport.close();
                }
                
                // Remove from map
                this.subscriptions.delete(key);
                // console.log(`[CHASE-ORDERBOOK] ✅ Closed ${key} subscription`);
                
            } catch (error) {
                // console.error(`[CHASE-ORDERBOOK] Error closing ${key}:`, error);
            }
        }
    }

    /**
     * Get current orderbook data for an asset
     * @param {string} asset - Asset symbol
     * @returns {object|null} Last orderbook data or null
     */
    getData(asset) {
        // For HIP-3 assets, preserve case (same as subscribe)
        const key = asset.includes(':') ? asset : asset.toUpperCase();
        const sub = this.subscriptions.get(key);
        return sub ? sub.lastData : null;
    }

    /**
     * Parse orderbook data from Hyperliquid format
     * Same logic as BrowserOrderbookTracker.handleOrderbookUpdate()
     * @private
     */
    _parseOrderbookData(data, asset) {
        try {
            // Parse Hyperliquid L2Book format: levels[0] = bids, levels[1] = asks
            let bids = [];
            let asks = [];

            if (data.levels && Array.isArray(data.levels) && data.levels.length >= 2) {
                // Convert Hyperliquid format to [price, size] arrays
                bids = data.levels[0].map(level => [level.px, level.sz]);
                asks = data.levels[1].map(level => [level.px, level.sz]);
            }

            const bestBid = bids[0]?.[0];
            const bestAsk = asks[0]?.[0];

            return {
                asset: asset,
                bids: bids,
                asks: asks,
                bestBid: bestBid,
                bestAsk: bestAsk,
                timestamp: Date.now()
            };

        } catch (error) {
            // console.error(`[CHASE-ORDERBOOK] Error parsing data for ${asset}:`, error);
            return null;
        }
    }

    /**
     * Get list of active subscriptions
     * @returns {Array<string>} Array of asset symbols
     */
    getActiveSubscriptions() {
        return Array.from(this.subscriptions.keys());
    }

    /**
     * Get subscription stats
     * @returns {object} Stats object
     */
    getStats() {
        const stats = {};
        for (const [asset, sub] of this.subscriptions.entries()) {
            stats[asset] = {
                listeners: sub.listeners.size,
                hasData: !!sub.lastData,
                lastUpdate: sub.lastData?.timestamp || null
            };
        }
        return stats;
    }
}

// Create global singleton
if (typeof window !== 'undefined') {
    window.ChaseOrderbookService = new ChaseOrderbookService();
    // console.log('✅ ChaseOrderbookService initialized');
}

export default ChaseOrderbookService;
